import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Pill } from "../ppm/ppmUtils";
import { TONE_CLASSES } from "../assets/assetUtils";
import { fmtDate, SectionText } from "./hseUtils";
import { Megaphone, CheckCircle, Warning } from "@phosphor-icons/react";

const SEV_TONE = { info: TONE_CLASSES.none, warning: TONE_CLASSES.soon, critical: TONE_CLASSES.overdue };
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

/** Safety bulletins sent to me — unsigned first; tap to read & sign. */
export function MyBulletinsList({ testid = "in-house-bulletins" }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => API.get("/hse/bulletins/mine").then((r) => setData(r.data)).catch(() => setData({ bulletins: [], unsigned: 0 })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const rows = data.bulletins;
  return (
    <div id="bulletins" className={`rounded-lg border p-5 ${data.unsigned ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" : "border-border bg-card"}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1"><Megaphone className="h-4 w-4" weight="bold" /> Safety bulletins</p>
          <p className="text-sm text-slate-700 dark:text-slate-200 mt-1" data-testid={`${testid}-summary`}>{data.unsigned ? `${data.unsigned} to read & sign` : rows.length ? "All signed — thank you" : "No bulletins yet"}</p>
        </div>
      </div>
      {rows.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {rows.slice(0, 8).map((b) => {
            const signed = !!b.mine?.signed_at;
            return (
              <li key={b.id}>
                <button type="button" onClick={() => setOpen(b)} className={`w-full text-left flex items-center justify-between gap-2 rounded-md border px-3 py-2.5 text-sm min-h-[44px] ${signed ? "border-border bg-card" : "border-amber-300 bg-card hover:border-primary/50"}`} data-testid={`${testid}-row-${b.id}`} data-signed={signed}>
                  <span className="min-w-0 flex-1">
                    <span className="font-medium block truncate">{b.title}</span>
                    <span className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap"><Pill tone={SEV_TONE[b.severity]}>{b.severity_label}</Pill>{fmtDate((b.sent_at || "").slice(0, 10))}{b.due && !signed ? ` · sign by ${fmtDate(b.due)}` : ""}</span>
                  </span>
                  <span className={`text-xs shrink-0 font-medium ${signed ? "text-emerald-700" : "text-amber-700"}`}>{signed ? <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Signed</span> : "Read & sign"}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <BulletinReadDialog bulletin={open} onClose={() => setOpen(null)} onSigned={load} testid={testid} />
    </div>
  );
}

export function BulletinReadDialog({ bulletin: b, onClose, onSigned, testid = "bulletin" }) {
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOk(false); }, [b?.id]);
  if (!b) return null;
  const signed = !!b.mine?.signed_at;
  const sign = async () => {
    setBusy(true);
    try { const { data } = await API.post(`/hse/bulletins/${b.id}/sign`, { confirm: true }); toast.success(data.message); onSigned?.(); onClose(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't sign"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!b} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid={`${testid}-read`}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Megaphone size={18} className={b.severity === "critical" ? "text-red-600" : "text-primary"} /> {b.title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><Pill tone={SEV_TONE[b.severity]}>{b.severity_label}</Pill><span className="font-mono">{b.reference}</span><span>sent {fmtWhen(b.sent_at)} by {b.sent_by_name}</span></div>
        {b.severity === "critical" && !signed && <p className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 p-2 text-xs text-red-800 dark:text-red-200 flex items-start gap-2"><Warning size={16} weight="fill" className="shrink-0 mt-0.5" /> Stop & read — please read this before your next job.</p>}
        <SectionText text={b.body} className="text-sm" />
        {b.key_points?.length > 0 && <div className="rounded-md border border-border p-3"><p className="text-xs font-semibold mb-1">By signing you confirm you will:</p><ul className="text-sm list-disc pl-5 space-y-0.5">{b.key_points.map((p, i) => <li key={i}>{p}</li>)}</ul></div>}
        {signed ? (
          <p className="text-sm text-emerald-700 inline-flex items-center gap-1" data-testid={`${testid}-signed-at`}><CheckCircle size={16} weight="fill" /> You signed this {fmtWhen(b.mine.signed_at)}</p>
        ) : (
          <>
            <label className="flex items-start gap-2 text-sm cursor-pointer rounded-md border border-border p-3"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid={`${testid}-confirm`} /><span>I have read and understood this safety bulletin and will follow it.</span></label>
            <DialogFooter><Button className="w-full sm:w-auto min-h-[44px]" onClick={sign} disabled={!ok || busy} data-testid={`${testid}-sign`}>{busy ? "Signing…" : "Sign"}</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
